import { Link, useSearchParams } from 'react-router-dom';
import { money } from '@/lib/utils';
import { imageSrc } from '@/lib/images';
import { isSoldOut, type Product } from '@/features/products/types';
import { copy } from '@/content/copy';

export function ProductCard({ product }: { product: Product }) {
  const soldOut = isSoldOut(product);
  const [cover, second] = product.images;
  const [params] = useSearchParams();

  // Keep the current filters when opening a product, so closing the dialog
  // returns to the same collection rather than resetting the grid.
  const next = new URLSearchParams(params);
  next.set('product', product.slug);

  return (
    <Link to={{ search: next.toString() }} className="group block overflow-hidden">
      <div className="relative aspect-[4/5] overflow-hidden border border-white/10 bg-panel">
        {/* Decorative: the heading below already names the product, and a
            second copy would repeat it in the link's accessible name. */}
        {cover ? (
          <img
            src={imageSrc(cover)}
            alt=""
            width={800}
            height={1000}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover saturate-[0.88] transition-transform duration-500 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="absolute inset-0 bg-[#111]" />
        )}

        {/* Second image is a desktop-only affordance: it doubles the download on
            phones where there is no hover state to reveal it. */}
        {second && (
          <img
            src={imageSrc(second)}
            alt=""
            aria-hidden
            width={800}
            height={1000}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 hidden h-full w-full object-cover opacity-0 transition-opacity duration-500 group-hover:opacity-100 [@media(hover:hover)]:block [@media(min-width:860px)]:block"
          />
        )}

        {soldOut && (
          <span className="absolute top-3 left-3 z-[1] bg-gold px-3 py-1 text-[0.78rem] text-night">
            {copy.product.soldOut}
          </span>
        )}
      </div>

      <h3 className="mt-3.5 font-serif text-[1.4rem] leading-tight text-cream max-[560px]:mt-2.5 max-[560px]:text-[1.12rem]">
        {product.name}
      </h3>
      <p className="font-medium text-gold-soft">{money(product.price)}</p>
    </Link>
  );
}
